import {
  Button,
  Icons,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiCaretLeft, PiCaretRight } from 'react-icons/pi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGPSLostAddGridToMap } from '../GpsLost/useGPSLostAddGridToMap';
import { useGPSLost } from '../GpsLost/GPSLostContext';
import { useDateTimeFormat } from '../../conversions/useDateFormat';

interface GPSLostLayerActionsProps {
  setDisableDragNnDropTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  isDragging: boolean;
}

export const GPSLostLayerActions = ({
  setDisableDragNnDropTooltip,
  isDragging,
}: GPSLostLayerActionsProps) => {
  const {
    selectedGPSLostConfiguration,
    selectedInstanceIndex,
    setSelectedInstanceIndex,
    instances,
  } = useGPSLost();
  const { addGridToMap, isAdding } = useGPSLostAddGridToMap();
  const [lastClicked, setLastClicked] = useState<string | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  // Calculate position relative to the trigger element
  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.top + rect.height / 2 - 20, // Center vertically and adjust for button height
          left: rect.left + 38, // Use left instead of right since trigger is already positioned -right-28
        });
      }
    };

    updatePosition();
  }, []);

  // Update position on scroll or resize
  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.top + rect.height / 2 - 20,
          left: rect.left + 38,
        });
      }
    };

    const timeoutId = setTimeout(updatePosition, 250);
    return () => clearTimeout(timeoutId);
  }, [isDragging]);

  const onNext = async () => {
    if (selectedInstanceIndex >= instances.length - 1) return;

    setLastClicked('next');
    const instance = instances[selectedInstanceIndex + 1];

    await addGridToMap(selectedGPSLostConfiguration, instance, false);
    setSelectedInstanceIndex(selectedInstanceIndex + 1);
    if (!isAdding) setLastClicked(null);
  };

  const onPrevious = async () => {
    if (selectedInstanceIndex <= 0) return;
    setLastClicked('previous');
    const instance = instances[selectedInstanceIndex - 1];
    await addGridToMap(selectedGPSLostConfiguration, instance, false);
    setSelectedInstanceIndex(selectedInstanceIndex - 1);
    if (!isAdding) setLastClicked(null);
  };

  const isPreviousDisabled = selectedInstanceIndex <= 0;
  const isNextDisabled = selectedInstanceIndex >= instances.length - 1;

  const isPreviousLoading = isAdding && lastClicked === 'previous';
  const isNextLoading = isAdding && lastClicked === 'next';

  const nextInstance = useMemo(() => {
    if (!instances || instances.length === 0) return null;

    if (selectedInstanceIndex >= instances.length - 1) return null;

    return instances[selectedInstanceIndex + 1];
  }, [instances, selectedInstanceIndex]);

  const previousInstance = useMemo(() => {
    if (!instances || instances.length === 0) return null;
    if (selectedInstanceIndex === 0) return null;
    return instances[selectedInstanceIndex - 1];
  }, [instances, selectedInstanceIndex]);

  const portalContent = (
    <div
      className="fixed flex space-x-1 z-50"
      style={{
        top: position.top,
        left: position.left,
      }}
      onMouseEnter={(e) => {
        e.stopPropagation();
        setDisableDragNnDropTooltip((prev) => (prev ? prev : true));
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        setDisableDragNnDropTooltip((prev) => (!prev ? prev : false));
      }}
    >
      <div className="flex items-center bg-background space-x-0.5 rounded-md shadow-lg border">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onPrevious}
              disabled={isPreviousDisabled || isAdding}
            >
              {!isPreviousLoading ? (
                <PiCaretLeft />
              ) : (
                <Icons.spinner className="h-4 w-4 animate-spin" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {previousInstance
              ? `Switch to GPS Lost grid for period between ${formatDateTimeWithTimeZone(
                  previousInstance.timerange?.lower
                )} and ${formatDateTimeWithTimeZone(
                  previousInstance.timerange?.upper
                )}`
              : ''}
          </TooltipContent>
        </Tooltip>
        <Separator orientation="vertical" className="h-4/6"></Separator>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onNext}
              disabled={isNextDisabled || isAdding}
            >
              {!isNextLoading ? (
                <PiCaretRight />
              ) : (
                <Icons.spinner className="h-4 w-4 animate-spin" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {nextInstance
              ? `Switch to GPS Lost grid for period between ${formatDateTimeWithTimeZone(
                  nextInstance.timerange?.lower
                )} and ${formatDateTimeWithTimeZone(
                  nextInstance.timerange?.upper
                )}`
              : ''}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );

  return (
    <>
      {/* Invisible trigger element that stays in the original position */}
      <div ref={triggerRef} className="absolute -right-28 w-28 h-10" />

      {/* Portal the actual content outside the scroll container */}
      {createPortal(portalContent, document.body)}
    </>
  );
};
